import { action, computed, makeObservable, observable, runInAction } from "mobx"
import {
    ACTION_COLORS,
    ACTION_ICONS,
    ACTION_VISIBILITY_CONDITIONS,
    ACTION_VISIBILITY_MISMATCH_BEHAVIORS,
    MAX_SAVED_ACTION_SESSION_TARGETS,
    SAVED_ACTION_SESSION_LAUNCH_KINDS,
    type SavedAction,
    type SavedActionColor,
    type SavedActionDraft,
    type SavedActionIcon,
    type SavedActionSessionLaunchKind,
    type SavedActionTemplate,
    type SavedActionsRecord,
    type SavedActionVisibilityCondition,
    type SavedActionVisibilityMismatchBehavior,
} from "../actions/actionTypes"
import { createInstalledDefaultAction, createInstalledDefaultActions, defaultActionTemplate, DEFAULT_ACTION_TEMPLATES } from "../actions/defaultActions"
import { retryBusy } from "../nest/appDataRetry"
import {
    NestClientError,
    type NestMethodParams,
    type NestMethodResult,
    type NestNotificationParams,
    type NestSubscriptionFilter,
    nestClient,
    requestNestMethod,
} from "../nest/client"
import { ulid } from "../utils/ulid"

export const SAVED_ACTIONS_NAMESPACE = "wildlark.actions"
export const SAVED_ACTIONS_KEY = "installed.v1"

const MAX_ACTIONS = 64
const MAX_ACTION_NAME_LENGTH = 80
const MAX_ACTION_PROMPT_LENGTH = 50_000
const MAX_SESSION_LAUNCH_DESCRIPTION_LENGTH = 500
const MAX_RECORD_BYTES = 240 * 1024
const LEGACY_CANONICAL_DEFAULT_ORDERS = [
    [
        "lets-do-it",
        "brief",
        "worktree",
        "council",
        "delegate",
        "preview",
        "independent-review",
        "fix-critical",
        "commit",
        "land",
        "push",
        "pull-request",
        "wait-on-pr",
        "deploy",
        "start-server",
        "check-back",
        "close",
        "abandon",
    ],
    [
        "lets-do-it",
        "fix-critical",
        "brief",
        "worktree",
        "preview",
        "start-server",
        "delegate",
        "independent-review",
        "clean-junk",
        "council",
        "commit",
        "land",
        "push",
        "pull-request",
        "check-back",
        "close",
        "abandon",
        "deploy",
    ],
    [
        "lets-do-it",
        "worktree",
        "delegate",
        "independent-review",
        "fix-critical",
        "council",
        "preview",
        "start-server",
        "commit",
        "land",
        "pull-request",
        "wait-on-pr",
        "push",
        "deploy",
        "check-back",
        "brief",
        "close",
        "abandon",
    ],
    [
        "lets-do-it",
        "fix-critical",
        "brief",
        "worktree",
        "preview",
        "start-server",
        "delegate",
        "independent-review",
        "council",
        "commit",
        "land",
        "pull-request",
        "fix-pr",
        "land-pr",
        "push",
        "deploy",
        "wait-on-pr",
        "check-back",
        "close",
        "abandon",
    ],
] as const

type SavedActionMethod = "appData.get" | "appData.set"
export type SavedActionRequest = <M extends SavedActionMethod>(method: M, params: NestMethodParams<M>) => Promise<NestMethodResult<M>>

export interface SavedActionSubscriptionClient {
    retainSubscriptions(filters: NestSubscriptionFilter[]): Promise<() => void>
    on(method: "appData.changed", handler: (params: NestNotificationParams<"appData.changed"> | undefined) => void): () => void
}

interface SavedActionStoreOptions {
    request?: SavedActionRequest
    client?: SavedActionSubscriptionClient
    now?: () => string
}

type SavedActionMutation =
    | { type: "create"; action: SavedAction }
    | {
          type: "update"
          actionId: string
          draft: SavedActionDraft
          updatedAt: string
      }
    | { type: "delete"; actionId: string }
    | {
          type: "set-enabled"
          actionId: string
          enabled: boolean
          updatedAt: string
      }
    | { type: "move"; actionId: string; offset: -1 | 1 }

const defaultRequest: SavedActionRequest = (method, params) => requestNestMethod(method, params)
const ACTION_ICON_SET = new Set<string>(ACTION_ICONS)
const ACTION_CONDITION_SET = new Set<string>(ACTION_VISIBILITY_CONDITIONS)
const ACTION_MISMATCH_BEHAVIOR_SET = new Set<string>(ACTION_VISIBILITY_MISMATCH_BEHAVIORS)
const ACTION_SESSION_LAUNCH_KIND_SET = new Set<string>(SAVED_ACTION_SESSION_LAUNCH_KINDS)
const LEGACY_ACTION_COLORS: Readonly<Record<string, SavedActionColor>> = {
    primary: "blue",
    secondary: "violet",
    accent: "fuchsia",
    neutral: "slate",
    info: "sky",
    success: "blue",
    warning: "amber",
    error: "rose",
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function savedActionColor(value: unknown): SavedActionColor | null {
    if (typeof value !== "string") return null
    return ACTION_COLORS.find((color) => color === value) ?? LEGACY_ACTION_COLORS[value] ?? null
}

function isSavedActionIcon(value: unknown): value is SavedActionIcon {
    return typeof value === "string" && ACTION_ICON_SET.has(value)
}

function isSavedActionVisibilityCondition(value: unknown): value is SavedActionVisibilityCondition {
    return typeof value === "string" && ACTION_CONDITION_SET.has(value)
}

function isSavedActionVisibilityMismatchBehavior(value: unknown): value is SavedActionVisibilityMismatchBehavior {
    return typeof value === "string" && ACTION_MISMATCH_BEHAVIOR_SET.has(value)
}

function isSavedActionSessionLaunchKind(value: unknown): value is SavedActionSessionLaunchKind {
    return typeof value === "string" && ACTION_SESSION_LAUNCH_KIND_SET.has(value)
}

function savedActionSessionLaunch(value: unknown): SavedAction["sessionLaunch"] | null {
    if (!isRecord(value) || !isSavedActionSessionLaunchKind(value.kind)) return null
    const description = value.description
    if (description !== undefined && (typeof description !== "string" || !description.trim() || description.length > MAX_SESSION_LAUNCH_DESCRIPTION_LENGTH)) {
        return null
    }
    const maxTargets = value.maxTargets
    if (
        maxTargets !== undefined &&
        (typeof maxTargets !== "number" || !Number.isInteger(maxTargets) || maxTargets < 1 || maxTargets > MAX_SAVED_ACTION_SESSION_TARGETS)
    ) {
        return null
    }
    return {
        kind: value.kind,
        ...(typeof description === "string" ? { description: description.trim() } : {}),
        ...(typeof maxTargets === "number" ? { maxTargets } : {}),
    }
}

function parseSavedAction(value: unknown): SavedAction | null {
    if (!isRecord(value)) return null
    const { id, name, prompt, icon, color, createdAt, updatedAt } = value
    if (typeof id !== "string" || !id || id.length > 120) return null
    if (typeof name !== "string" || !name.trim() || name.length > MAX_ACTION_NAME_LENGTH) return null
    if (typeof prompt !== "string" || !prompt.trim() || prompt.length > MAX_ACTION_PROMPT_LENGTH) return null
    const parsedColor = savedActionColor(color)
    if (!isSavedActionIcon(icon) || !parsedColor) return null
    if (typeof createdAt !== "string" || !createdAt || typeof updatedAt !== "string" || !updatedAt) return null
    const enabled = value.enabled === undefined ? true : value.enabled
    if (typeof enabled !== "boolean") return null
    const requiresConfirmation = value.requiresConfirmation === undefined ? false : value.requiresConfirmation
    if (typeof requiresConfirmation !== "boolean") return null

    const visibility = value.visibility
    if (!isRecord(visibility) || (visibility.match !== "all" && visibility.match !== "any") || !Array.isArray(visibility.conditions)) return null
    if (!visibility.conditions.every(isSavedActionVisibilityCondition)) return null
    const onMismatch = visibility.onMismatch ?? "disable"
    if (!isSavedActionVisibilityMismatchBehavior(onMismatch)) return null

    let source: SavedAction["source"]
    if (value.source !== undefined) {
        if (!isRecord(value.source)) return null
        if (typeof value.source.templateId !== "string" || !value.source.templateId) return null
        if (typeof value.source.templateVersion !== "number" || !Number.isInteger(value.source.templateVersion) || value.source.templateVersion < 1) return null
        source = {
            templateId: value.source.templateId,
            templateVersion: value.source.templateVersion,
        }
    }

    let sessionLaunch: SavedAction["sessionLaunch"]
    if (value.sessionLaunch !== undefined) {
        sessionLaunch = savedActionSessionLaunch(value.sessionLaunch) ?? undefined
        if (!sessionLaunch) return null
    }

    const allowCombining = value.allowCombining === undefined ? !sessionLaunch : value.allowCombining
    if (typeof allowCombining !== "boolean" || (sessionLaunch && allowCombining)) return null

    return {
        id,
        name: name.trim(),
        prompt,
        icon,
        color: parsedColor,
        enabled,
        requiresConfirmation,
        allowCombining,
        visibility: {
            match: visibility.match,
            conditions: [...visibility.conditions],
            onMismatch,
        },
        ...(sessionLaunch ? { sessionLaunch } : {}),
        ...(source ? { source } : {}),
        createdAt,
        updatedAt,
    }
}

export function parseSavedActionsRecord(value: unknown): SavedActionsRecord | null {
    if (!isRecord(value) || value.v !== 1 || !Array.isArray(value.actions) || typeof value.updatedAt !== "string") return null
    if (value.actions.length > MAX_ACTIONS) return null

    const actions: SavedAction[] = []
    const ids = new Set<string>()
    for (const candidate of value.actions) {
        const parsed = parseSavedAction(candidate)
        if (!parsed || ids.has(parsed.id)) return null
        ids.add(parsed.id)
        actions.push(parsed)
    }

    const record = { v: 1 as const, actions, updatedAt: value.updatedAt }
    return serializedRecordSize(record) <= MAX_RECORD_BYTES ? record : null
}

function serializedRecordSize(record: SavedActionsRecord): number {
    return new TextEncoder().encode(JSON.stringify(record)).byteLength
}

function validateSavedActionsRecord(record: SavedActionsRecord): void {
    if (record.actions.length > MAX_ACTIONS) throw new Error(`Actions are limited to ${MAX_ACTIONS}`)
    if (record.actions.some((item) => !item.name.trim() || item.name.length > MAX_ACTION_NAME_LENGTH)) {
        throw new Error(`Action names must be between 1 and ${MAX_ACTION_NAME_LENGTH} characters`)
    }
    if (record.actions.some((item) => !item.prompt.trim() || item.prompt.length > MAX_ACTION_PROMPT_LENGTH)) {
        throw new Error(`Action messages must be between 1 and ${MAX_ACTION_PROMPT_LENGTH} characters`)
    }
    if (record.actions.some((item) => item.sessionLaunch && item.allowCombining)) {
        throw new Error("Agent-picker Actions cannot combine with other Actions")
    }
    if (
        record.actions.some(
            (item) =>
                item.sessionLaunch?.description !== undefined &&
                (!item.sessionLaunch.description.trim() || item.sessionLaunch.description.length > MAX_SESSION_LAUNCH_DESCRIPTION_LENGTH)
        )
    ) {
        throw new Error(`Agent-picker descriptions are limited to ${MAX_SESSION_LAUNCH_DESCRIPTION_LENGTH} characters`)
    }
    if (
        record.actions.some(
            (item) =>
                item.sessionLaunch?.maxTargets !== undefined &&
                (!Number.isInteger(item.sessionLaunch.maxTargets) ||
                    item.sessionLaunch.maxTargets < 1 ||
                    item.sessionLaunch.maxTargets > MAX_SAVED_ACTION_SESSION_TARGETS)
        )
    ) {
        throw new Error(`Agent-picker target limits must be between 1 and ${MAX_SAVED_ACTION_SESSION_TARGETS}`)
    }
    if (serializedRecordSize(record) > MAX_RECORD_BYTES) throw new Error("The installed Actions record is too large")
}

function clone<T>(value: T): T {
    return globalThis.structuredClone(value)
}

function migrateCanonicalDefaultOrder(actions: readonly SavedAction[]): readonly SavedAction[] {
    const liveTemplateIds = new Set(DEFAULT_ACTION_TEMPLATES.map((template) => template.templateId))
    const existingTemplateOrder = actions.flatMap((action) => {
        const templateId = action.source?.templateId
        return templateId && liveTemplateIds.has(templateId) ? [templateId] : []
    })
    const existingTemplateIds = new Set(existingTemplateOrder)
    const matchesLegacyOrder = LEGACY_CANONICAL_DEFAULT_ORDERS.some((legacyOrder) => {
        const legacyTemplateIds = new Set<string>(legacyOrder)
        const comparableExistingOrder = existingTemplateOrder.filter((templateId) => legacyTemplateIds.has(templateId))
        const expectedExistingOrder = legacyOrder.filter((templateId) => existingTemplateIds.has(templateId))
        return (
            comparableExistingOrder.length > 0 &&
            comparableExistingOrder.length === expectedExistingOrder.length &&
            comparableExistingOrder.every((templateId, index) => templateId === expectedExistingOrder[index])
        )
    })
    if (!matchesLegacyOrder) {
        return actions
    }

    const actionsByTemplateId = new Map(actions.flatMap((action) => (action.source ? [[action.source.templateId, action] as const] : [])))
    const orderedDefaults = DEFAULT_ACTION_TEMPLATES.flatMap((template) => {
        const action = actionsByTemplateId.get(template.templateId)
        return action ? [action] : []
    })
    if (orderedDefaults.length !== existingTemplateOrder.length) return actions

    let defaultIndex = 0
    return actions.map((action) => {
        if (!action.source || !liveTemplateIds.has(action.source.templateId)) return action
        const orderedAction = orderedDefaults[defaultIndex]
        defaultIndex += 1
        return orderedAction
    })
}

function reconcileDefaultActions(actions: readonly SavedAction[], now: string): SavedAction[] {
    const reconciled: SavedAction[] = []
    const seenTemplateIds = new Set<string>()

    for (const savedAction of migrateCanonicalDefaultOrder(actions)) {
        const source = savedAction.source
        if (!source) {
            reconciled.push(clone(savedAction))
            continue
        }

        const template = defaultActionTemplate(source.templateId)
        if (!template || seenTemplateIds.has(template.templateId)) continue
        seenTemplateIds.add(template.templateId)
        reconciled.push({
            id: savedAction.id,
            ...clone(template.action),
            enabled:
                template.enableOnUpgradeThroughVersion !== undefined && source.templateVersion <= template.enableOnUpgradeThroughVersion
                    ? true
                    : savedAction.enabled,
            source: {
                templateId: template.templateId,
                templateVersion: template.templateVersion,
            },
            createdAt: savedAction.createdAt,
            updatedAt: savedAction.updatedAt,
        })
    }

    let pendingTemplates = DEFAULT_ACTION_TEMPLATES.filter((template) => !seenTemplateIds.has(template.templateId))
    while (pendingTemplates.length > 0) {
        const pendingTemplateIds = new Set(pendingTemplates.map((template) => template.templateId))
        const deferredTemplates: SavedActionTemplate[] = []

        for (const template of pendingTemplates) {
            if (template.insertBeforeTemplateId && pendingTemplateIds.has(template.insertBeforeTemplateId)) {
                deferredTemplates.push(template)
                continue
            }

            const installedAction = createInstalledDefaultAction(template, now)
            const insertBeforeIndex = template.insertBeforeTemplateId
                ? reconciled.findIndex((action) => action.source?.templateId === template.insertBeforeTemplateId)
                : -1
            if (insertBeforeIndex === -1) {
                reconciled.push(installedAction)
            } else {
                reconciled.splice(insertBeforeIndex, 0, installedAction)
            }
        }

        if (deferredTemplates.length === pendingTemplates.length) {
            reconciled.push(...deferredTemplates.map((template) => createInstalledDefaultAction(template, now)))
            break
        }
        pendingTemplates = deferredTemplates
    }

    return reconciled
}

function applyMutation(actions: SavedAction[], mutation: SavedActionMutation): SavedAction[] {
    const next = actions.map(clone)

    switch (mutation.type) {
        case "create":
            if (next.some((item) => item.id === mutation.action.id)) return next
            next.push(clone(mutation.action))
            return next
        case "update": {
            const index = next.findIndex((item) => item.id === mutation.actionId)
            if (index === -1) throw new Error("Action no longer exists")
            const current = next[index]
            if (current.source) throw new Error("Duplicate a default Action before editing it")
            next[index] = {
                ...current,
                ...clone(mutation.draft),
                source: current.source,
                updatedAt: mutation.updatedAt,
            }
            return next
        }
        case "delete": {
            const current = next.find((item) => item.id === mutation.actionId)
            if (current?.source) throw new Error("Default Actions can be turned off, but not removed")
            return next.filter((item) => item.id !== mutation.actionId)
        }
        case "set-enabled": {
            const index = next.findIndex((item) => item.id === mutation.actionId)
            if (index === -1) throw new Error("Action no longer exists")
            next[index] = {
                ...next[index],
                enabled: mutation.enabled,
                updatedAt: mutation.updatedAt,
            }
            return next
        }
        case "move": {
            const index = next.findIndex((item) => item.id === mutation.actionId)
            if (index === -1) return next
            const target = index + mutation.offset
            if (target < 0 || target >= next.length) return next
            const [moved] = next.splice(index, 1)
            next.splice(target, 0, moved)
            return next
        }
    }
}

export class SavedActionStore {
    private actions: SavedAction[] = []
    private version: number | undefined
    private readonly request: SavedActionRequest
    private readonly client: SavedActionSubscriptionClient
    private readonly now: () => string
    private writeQueue: Promise<void> = Promise.resolve()
    private notificationDisposer: (() => void) | null = null
    private subscriptionDisposer: (() => void) | null = null
    private notificationArrivedDuringLoad = false
    loaded = false
    error: string | null = null

    constructor(options: SavedActionStoreOptions = {}) {
        this.request = options.request ?? defaultRequest
        this.client = options.client ?? nestClient
        this.now = options.now ?? (() => new Date().toISOString())
        makeObservable<SavedActionStore, "actions" | "replaceLocal">(this, {
            actions: observable.shallow,
            loaded: observable,
            error: observable,
            installedActions: computed,
            replaceLocal: action,
        })
    }

    get installedActions(): readonly SavedAction[] {
        return this.actions
    }

    getAction(actionId: string): SavedAction | null {
        const savedAction = this.actions.find((item) => item.id === actionId)
        return savedAction ? clone(savedAction) : null
    }

    /**
     * Installs the product defaults in memory only. Guests on a colleague's
     * computer cannot read or write that node's saved Actions, and they do
     * not run Actions there anyway.
     */
    loadLocalDefaults(): void {
        if (this.loaded) return
        this.replaceLocal(createInstalledDefaultActions(this.now()), null)
    }

    async load(): Promise<void> {
        if (this.loaded) return
        try {
            await this.ensureSubscription()
            const result = await retryBusy(() =>
                this.request("appData.get", {
                    namespace: SAVED_ACTIONS_NAMESPACE,
                    key: SAVED_ACTIONS_KEY,
                })
            )

            if (result.found) {
                this.loadResult(result)
            } else {
                await this.seedDefaults()
            }

            if (this.notificationArrivedDuringLoad) {
                this.notificationArrivedDuringLoad = false
                await this.refresh()
            }
        } catch (loadError) {
            if (!this.loaded) this.dispose()
            throw loadError
        }
    }

    dispose(): void {
        this.notificationDisposer?.()
        this.notificationDisposer = null
        this.subscriptionDisposer?.()
        this.subscriptionDisposer = null
    }

    createAction(draft: SavedActionDraft): Promise<void> {
        const timestamp = this.now()
        return this.mutate({
            type: "create",
            action: {
                id: `custom.${ulid()}`,
                ...clone(draft),
                enabled: true,
                createdAt: timestamp,
                updatedAt: timestamp,
            },
        })
    }

    updateAction(actionId: string, draft: SavedActionDraft): Promise<void> {
        return this.mutate({
            type: "update",
            actionId,
            draft: clone(draft),
            updatedAt: this.now(),
        })
    }

    deleteAction(actionId: string): Promise<void> {
        return this.mutate({ type: "delete", actionId })
    }

    setActionEnabled(actionId: string, enabled: boolean): Promise<void> {
        return this.mutate({
            type: "set-enabled",
            actionId,
            enabled,
            updatedAt: this.now(),
        })
    }

    moveAction(actionId: string, offset: -1 | 1): Promise<void> {
        return this.mutate({ type: "move", actionId, offset })
    }

    private async ensureSubscription(): Promise<void> {
        if (this.subscriptionDisposer) return
        this.notificationDisposer = this.client.on("appData.changed", (params) => {
            if (params?.namespace !== SAVED_ACTIONS_NAMESPACE || params.key !== SAVED_ACTIONS_KEY) return
            if (!this.loaded) {
                this.notificationArrivedDuringLoad = true
                return
            }
            const refresh = () => this.refresh()
            const next = this.writeQueue.then(refresh, refresh)
            this.writeQueue = next.catch((refreshError) => {
                console.warn("[SavedActionStore] Failed to refresh Actions:", refreshError)
            })
        })
        try {
            this.subscriptionDisposer = await this.client.retainSubscriptions([
                {
                    method: "appData.changed",
                    appDataNamespace: SAVED_ACTIONS_NAMESPACE,
                    appDataPrefix: SAVED_ACTIONS_KEY,
                },
            ])
        } catch (subscriptionError) {
            this.notificationDisposer?.()
            this.notificationDisposer = null
            throw subscriptionError
        }
    }

    private async seedDefaults(): Promise<void> {
        const timestamp = this.now()
        const record: SavedActionsRecord = {
            v: 1,
            actions: createInstalledDefaultActions(timestamp),
            updatedAt: timestamp,
        }
        try {
            const result = await retryBusy(() =>
                this.request("appData.set", {
                    namespace: SAVED_ACTIONS_NAMESPACE,
                    key: SAVED_ACTIONS_KEY,
                    expectedVersion: 0,
                    value: record,
                })
            )
            this.version = result.version
            this.replaceLocal(record.actions, null)
        } catch (seedError) {
            if (!(seedError instanceof NestClientError) || seedError.code !== "version_conflict") throw seedError
            await this.refresh()
        }
    }

    private loadResult(result: NestMethodResult<"appData.get">): void {
        this.version = result.version
        const record = parseSavedActionsRecord(result.value)
        if (!record) {
            this.replaceLocal([], "Installed Actions use an unsupported or invalid data format. No changes were made.")
            return
        }
        this.replaceLocal(record.actions, null)
    }

    private async refresh(): Promise<void> {
        const result = await retryBusy(() =>
            this.request("appData.get", {
                namespace: SAVED_ACTIONS_NAMESPACE,
                key: SAVED_ACTIONS_KEY,
            })
        )
        if (!result.found) {
            this.version = undefined
            this.replaceLocal([], null)
            return
        }
        this.loadResult(result)
    }

    private mutate(mutation: SavedActionMutation): Promise<void> {
        const run = () => this.writeMutation(mutation, true)
        const result = this.writeQueue.then(run, run)
        this.writeQueue = result.catch(() => undefined)
        return result
    }

    private async writeMutation(mutation: SavedActionMutation, retryConflict: boolean): Promise<void> {
        if (!this.loaded || this.error) throw new Error(this.error ?? "Actions have not loaded yet")
        const actions = applyMutation(this.actions, mutation)
        const record: SavedActionsRecord = {
            v: 1,
            actions,
            updatedAt: this.now(),
        }
        validateSavedActionsRecord(record)

        try {
            const result = await retryBusy(() =>
                this.request("appData.set", {
                    namespace: SAVED_ACTIONS_NAMESPACE,
                    key: SAVED_ACTIONS_KEY,
                    expectedVersion: this.version ?? 0,
                    value: record,
                })
            )
            this.version = result.version
            this.replaceLocal(actions, null)
        } catch (writeError) {
            if (!retryConflict || !(writeError instanceof NestClientError) || writeError.code !== "version_conflict") throw writeError
            await this.refresh()
            await this.writeMutation(mutation, false)
        }
    }

    private replaceLocal(actions: SavedAction[], error: string | null): void {
        runInAction(() => {
            this.actions = reconcileDefaultActions(actions, this.now())
            this.error = error
            this.loaded = true
        })
    }
}
